import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listReorderPolicies, upsertReorderPolicy, deleteReorderPolicy, skuPicker } from "@/lib/forecasting.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/forecasting/policies")({ component: PoliciesPage });

function PoliciesPage() {
  const { tenantId, facilityId } = useScope();
  const listFn = useServerFn(listReorderPolicies);
  const upsertFn = useServerFn(upsertReorderPolicy);
  const delFn = useServerFn(deleteReorderPolicy);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["reorder-policies", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id, tenantId: tenantId! } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["reorder-policies"] }); },
  });

  const rows = data?.rows ?? [];

  return (
    <>
      <PageHeader title="Reorder Policies"
        description="Per-SKU reorder rules. Choose a static reorder point, or let the system calculate one from min stock + run rate × lead time."
        actions={<NewPolicyDialog onCreated={() => { qc.invalidateQueries({ queryKey: ["reorder-policies"] }); }} upsertFn={upsertFn} tenantId={tenantId} facilityId={facilityId} />}
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Lead time</TableHead>
                    <TableHead className="text-right">Min / Run rate</TableHead>
                    <TableHead className="text-right">ROP</TableHead>
                    <TableHead className="text-right">Reorder qty</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
                  {!isLoading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No reorder policies yet. Create one to enable purchase suggestions.</TableCell></TableRow>
                  )}
                  {rows.map((p) => {
                    const sku = p.skus as { sku_code: string; name: string } | null;
                    const mode = (p as { mode?: "static" | "calculated" }).mode ?? "calculated";
                    const minStock = (p as { min_stock_qty?: number }).min_stock_qty ?? 0;
                    const runRate = (p as { run_rate_per_day?: number | null }).run_rate_per_day;
                    return (
                      <TableRow key={p.id}>
                        <TableCell>
                          <div className="font-mono text-xs">{sku?.sku_code ?? p.sku_id.slice(0, 8)}</div>
                          <div className="text-xs text-muted-foreground">{sku?.name ?? ""}</div>
                        </TableCell>
                        <TableCell><Badge variant={mode === "static" ? "outline" : "secondary"}>{mode}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{p.lead_time_days}d</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {mode === "static"
                            ? <span className="text-muted-foreground">—</span>
                            : <>min {minStock} · {runRate != null ? `${Number(runRate).toFixed(1)}/d` : "auto"}</>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{p.reorder_point}</TableCell>
                        <TableCell className="text-right tabular-nums">{p.reorder_qty}</TableCell>
                        <TableCell className="text-sm">{p.supplier_name ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell><Badge variant={p.active ? "default" : "outline"}>{p.active ? "Active" : "Paused"}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => del.mutate(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function NewPolicyDialog({ onCreated, upsertFn, tenantId, facilityId }: {
  onCreated: () => void;
  upsertFn: ReturnType<typeof useServerFn<typeof upsertReorderPolicy>>;
  tenantId: string | null; facilityId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [skuQuery, setSkuQuery] = useState("");
  const [skuId, setSkuId] = useState<string | null>(null);
  const [mode, setMode] = useState<"static" | "calculated">("calculated");
  const [leadTime, setLeadTime] = useState(14);
  const [reorderQty, setReorderQty] = useState(100);
  const [reorderPoint, setReorderPoint] = useState(0);
  const [minStock, setMinStock] = useState(0);
  const [runRate, setRunRate] = useState<number | "">("");
  const [supplier, setSupplier] = useState("");
  const [unitCost, setUnitCost] = useState(0);
  const [active, setActive] = useState(true);

  const pickFn = useServerFn(skuPicker);
  const { data: skus } = useQuery({
    queryKey: ["sku-picker", tenantId, skuQuery],
    enabled: !!tenantId && open,
    queryFn: () => pickFn({ data: { tenantId: tenantId!, query: skuQuery } }),
  });

  const save = useMutation({
    mutationFn: () => upsertFn({ data: {
      tenantId: tenantId!, facilityId: facilityId!, skuId: skuId!,
      mode,
      leadTimeDays: leadTime,
      serviceLevel: 0.95,
      reorderQty,
      reorderPoint: mode === "static" ? reorderPoint : 0,
      minStockQty: mode === "calculated" ? minStock : 0,
      runRatePerDay: mode === "calculated" && runRate !== "" ? Number(runRate) : null,
      supplierName: supplier || null, unitCostCents: Math.round(unitCost * 100), active,
    } }),
    onSuccess: () => { toast.success("Policy saved"); setOpen(false); onCreated(); },
    onError: (e) => toast.error(String(e)),
  });

  const previewRop = mode === "calculated"
    ? Math.ceil(minStock + (runRate === "" ? 0 : Number(runRate)) * leadTime)
    : reorderPoint;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={!tenantId || !facilityId}><Plus className="h-4 w-4 mr-2" />New policy</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New reorder policy</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>SKU</Label>
            <Input placeholder="Search by code or name…" value={skuQuery} onChange={(e) => { setSkuQuery(e.target.value); setSkuId(null); }} />
            {open && (skus?.rows.length ?? 0) > 0 && !skuId && (
              <div className="mt-1 border rounded max-h-40 overflow-y-auto text-sm">
                {skus?.rows.map((s) => (
                  <button key={s.id} type="button"
                    className="w-full text-left px-2 py-1.5 hover:bg-accent"
                    onClick={() => { setSkuId(s.id); setSkuQuery(`${s.sku_code} — ${s.name}`); }}>
                    <span className="font-mono text-xs">{s.sku_code}</span> <span className="text-muted-foreground">— {s.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Reorder logic</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as "static" | "calculated")} className="grid grid-cols-2 gap-2 mt-1">
              <label className={`flex items-start gap-2 rounded border p-2 cursor-pointer ${mode === "static" ? "border-primary" : ""}`}>
                <RadioGroupItem value="static" className="mt-0.5" />
                <div className="text-xs"><div className="font-medium">Static</div><div className="text-muted-foreground">Fixed reorder point you set.</div></div>
              </label>
              <label className={`flex items-start gap-2 rounded border p-2 cursor-pointer ${mode === "calculated" ? "border-primary" : ""}`}>
                <RadioGroupItem value="calculated" className="mt-0.5" />
                <div className="text-xs"><div className="font-medium">Calculated</div><div className="text-muted-foreground">min stock + run rate × lead time.</div></div>
              </label>
            </RadioGroup>
          </div>
          {mode === "static" ? (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Reorder point</Label><Input type="number" value={reorderPoint} onChange={(e) => setReorderPoint(Number(e.target.value))} /></div>
              <div><Label>Lead time (days)</Label><Input type="number" value={leadTime} onChange={(e) => setLeadTime(Number(e.target.value))} /></div>
              <div><Label>Reorder qty</Label><Input type="number" value={reorderQty} onChange={(e) => setReorderQty(Number(e.target.value))} /></div>
              <div><Label>Unit cost ($)</Label><Input type="number" step={0.01} value={unitCost} onChange={(e) => setUnitCost(Number(e.target.value))} /></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Min stock</Label><Input type="number" value={minStock} onChange={(e) => setMinStock(Number(e.target.value))} /></div>
                <div><Label>Lead time (days)</Label><Input type="number" value={leadTime} onChange={(e) => setLeadTime(Number(e.target.value))} /></div>
                <div><Label>Run rate (/day)</Label><Input type="number" step={0.1} placeholder="auto" value={runRate} onChange={(e) => setRunRate(e.target.value === "" ? "" : Number(e.target.value))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Reorder qty</Label><Input type="number" value={reorderQty} onChange={(e) => setReorderQty(Number(e.target.value))} /></div>
                <div><Label>Unit cost ($)</Label><Input type="number" step={0.01} value={unitCost} onChange={(e) => setUnitCost(Number(e.target.value))} /></div>
              </div>
              <div className="text-xs text-muted-foreground">Computed reorder point: <span className="font-mono tabular-nums text-foreground">{previewRop}</span>{runRate === "" && <span> (run rate falls back to observed demand at forecast time)</span>}</div>
            </>
          )}
          <div><Label>Supplier name</Label><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Optional" /></div>
          <div className="flex items-center gap-2"><Switch checked={active} onCheckedChange={setActive} /><Label>Active</Label></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!skuId || save.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}